// Layer: Page (UI) – chỉ render JSX, gọi hook domain từ features.
import React, { useMemo, memo, useState, useEffect } from "react";
import {
  Table,
  Typography,
  Tag,
  Button,
  Select,
  DatePicker,
  Input,
  Descriptions,
  Modal,
  Tooltip,
  Avatar,
  Spin,
} from "antd";
import {
  Activity,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
  Calendar,
  Monitor,
  Globe,
  FileText,
} from "lucide-react";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import "dayjs/locale/vi";
import type { ActivityLog } from "../services/activity-logs.service";
import { Pagination } from "../components/common/Pagination";
import { FilterSection } from "../components/common/FilterSection";
import { buildFullUrl } from "../contexts/AvatarContext";
import { formatDateTimeDisplay } from "../utils/dateHelpers";
import {
  useActivityLogs,
  type UseActivityLogsResult,
} from "@/features/activity-logs/useActivityLogs";
import searchIcon from "../assets/search-icon.svg";
import styles from "./ActivityLogsPage.module.css";

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

dayjs.locale("vi");

/**
 * Queue quản lý tải avatar (tránh quá tải server/HTTP 429).
 * Mục tiêu: UI render nhanh, request chạy nền và có giới hạn đồng thời.
 */
class AvatarRequestQueue {
  private queue: Array<{
    url: string;
    resolve: (value: string | null) => void;
    reject: (error: any) => void;
  }> = [];
  private activeRequests = 0;
  private readonly maxConcurrent = 3; // Số request đồng thời tối đa
  private readonly delayBetweenRequestsMs = 25; // Delay nhỏ để giảm nguy cơ burst

  async add(
    url: string,
    fetchFn: (url: string) => Promise<string | null>,
  ): Promise<string | null> {
    return new Promise((resolve, reject) => {
      this.queue.push({ url, resolve, reject });
      this.processQueue(fetchFn);
    });
  }

  private async processQueue(fetchFn: (url: string) => Promise<string | null>) {
    // Tránh recursion (dễ tạo call stack sâu khi nhiều item + lỗi)
    while (this.queue.length > 0 && this.activeRequests < this.maxConcurrent) {
      const item = this.queue.shift();
      if (!item) return;

      this.activeRequests += 1;

      (async () => {
        try {
          const result = await fetchFn(item.url);
          item.resolve(result);
        } catch (error) {
          item.reject(error);
        } finally {
          this.activeRequests -= 1;
          if (this.queue.length > 0) {
            await new Promise((resolve) =>
              setTimeout(resolve, this.delayBetweenRequestsMs),
            );
            this.processQueue(fetchFn);
          }
        }
      })();
    }
  }
}

// Instance queue dùng chung cho toàn bộ ứng dụng
const avatarRequestQueue = new AvatarRequestQueue();

/**
 * Props cho component ActivityLogAvatar
 */
interface ActivityLogAvatarProps {
  avatarUrl: string | null | undefined; // URL avatar (có thể là relative hoặc full URL)
  fetchAvatarAsDataUri: (url: string) => Promise<string | null>; // Function để fetch avatar và convert sang data URI
  cacheRef: React.MutableRefObject<Map<string, string>>; // Ref chứa cache avatar đã load
  size?: number; // Kích thước avatar (mặc định 32px)
}

/**
 * Component hiển thị avatar tối ưu hiệu năng:
 * - Render URL trực tiếp ngay (nhanh, không chặn UI)
 * - Đồng thời tải dataURI nền (khi cần auth) và cache lại
 * - Fallback an toàn khi lỗi
 */
const ActivityLogAvatar: React.FC<ActivityLogAvatarProps> = memo(
  ({ avatarUrl, fetchAvatarAsDataUri, cacheRef, size = 32 }) => {
    // Data URI (ưu tiên hiển thị sau khi load thành công vì đảm bảo có Authorization)
    const [avatarDataUri, setAvatarDataUri] = useState<string | null>(null);
    // Dùng URL trực tiếp để render nhanh ngay từ đầu (sau đó có thể thay bằng dataURI)
    const [useDirectUrl, setUseDirectUrl] = useState(true);
    // Cờ lỗi của thẻ <img> bên trong Avatar
    const [avatarError, setAvatarError] = useState(false);

    useEffect(() => {
      // Reset state khi avatarUrl thay đổi
      setAvatarDataUri(null);
      setAvatarError(false);
      setUseDirectUrl(true);

      if (!avatarUrl) {
        return;
      }

      // Kiểm tra URL hợp lệ
      const isValidUrl =
        avatarUrl.startsWith("http://") || avatarUrl.startsWith("https://");
      if (!isValidUrl) {
        return;
      }

      // Kiểm tra cache trước (tránh fetch lại không cần thiết)
      if (cacheRef.current.has(avatarUrl)) {
        const cached = cacheRef.current.get(avatarUrl);
        if (cached) {
          setAvatarDataUri(cached);
          setAvatarError(false);
          setUseDirectUrl(false);
          return;
        }
      }

      // Sử dụng request queue để throttle requests và tránh 429 errors
      avatarRequestQueue
        .add(avatarUrl, fetchAvatarAsDataUri)
        .then((dataUri) => {
          if (dataUri) {
            // Cache kết quả để dùng lại lần sau
            cacheRef.current.set(avatarUrl, dataUri);
            setAvatarDataUri(dataUri);
            setAvatarError(false);
            setUseDirectUrl(false);
          } else {
            // Fallback: giữ URL trực tiếp nếu fetch data URI thất bại
            setUseDirectUrl(true);
            setAvatarError(false);
          }
        })
        .catch(() => {
          // Fallback: giữ URL trực tiếp khi có lỗi
          setUseDirectUrl(true);
          setAvatarError(false);
        });
    }, [avatarUrl, fetchAvatarAsDataUri, cacheRef]);

    // Xác định source cho avatar: ưu tiên data URI, sau đó là URL trực tiếp
    const avatarSrc =
      avatarDataUri && !avatarError
        ? avatarDataUri
        : useDirectUrl && avatarUrl
          ? avatarUrl
          : undefined;
    const showAvatar = !!avatarSrc;
    const iconSize = size <= 32 ? 16 : 20; // Kích thước icon User phụ thuộc vào size avatar

    return (
      <Avatar
        size={size}
        src={showAvatar ? avatarSrc : undefined}
        icon={!showAvatar ? <User size={iconSize} /> : undefined}
        className={styles.avatar}
        onError={() => {
          // Nếu dataURI lỗi, fallback về URL trực tiếp (nhanh và ít rủi ro hơn)
          if (avatarDataUri && !useDirectUrl) {
            setUseDirectUrl(true);
            setAvatarDataUri(null);
            setAvatarError(false);
          } else {
            setAvatarError(true);
          }
          return false;
        }}
      />
    );
  },
);

ActivityLogAvatar.displayName = "ActivityLogAvatar";

/**
 * Mapping loại tài nguyên sang tiếng Việt
 */
const resourceTypeLabels: Record<string, string> = {
  user: "Người dùng",
  department: "Phòng ban",
  company: "Công ty",
  other: "Khác",
};

/**
 * Cấu hình hiển thị kết quả hoạt động (màu sắc, icon, label)
 */
const resultConfig: Record<
  string,
  { label: string; color: string; icon: React.ReactNode }
> = {
  success: {
    label: "Thành công",
    color: "success",
    icon: <CheckCircle size={14} />,
  },
  failure: {
    label: "Thất bại",
    color: "error",
    icon: <XCircle size={14} />,
  },
  warning: {
    label: "Cảnh báo",
    color: "warning",
    icon: <AlertCircle size={14} />,
  },
};

/**
 * Mapping mã hành động sang tiếng Việt
 */
const actionLabels: Record<string, string> = {
  LOGIN_SUCCESS: "Đăng nhập thành công",
  LOGIN_FAILED: "Đăng nhập thất bại",
  VIEW_ACTIVITY_LOGS: "Xem nhật ký hoạt động",
  VIEW_ACTIVITY_LOGS_STATS: "Xem thống kê nhật ký hoạt động",
  VIEW_LOGIN_LOGS: "Xem nhật ký đăng nhập",
  VIEW_RESOURCE_LOGS: "Xem nhật ký tài nguyên",
  UPDATE_USER: "Cập nhật người dùng",
  DELETE_USER: "Xóa người dùng",
  CREATE_USER: "Tạo người dùng",
  CREATE_COMPANY: "Tạo công ty",
  CREATE_GPS_LOCATION: "Tạo vị trí GPS",
  CREATE_WORK_SHIFT: "Tạo ca làm",
  CREATE_ATTENDANCE_MANUAL: "Tạo chấm công thủ công",
  CHANGE_PASSWORD: "Đổi mật khẩu",
  VIEW_USER_PROFILE: "Xem hồ sơ người dùng",
  BLOCK_USER: "Khóa người dùng",
  UNBLOCK_USER: "Mở khóa người dùng",
  PERMANENT_DELETE_USER: "Xóa vĩnh viễn người dùng",
  RESTORE_USER: "Khôi phục người dùng",
  REQUEST_USER_UPDATE: "Yêu cầu cập nhật thông tin",
  APPROVE_USER_UPDATE: "Phê duyệt cập nhật",
  REJECT_USER_UPDATE: "Từ chối cập nhật",
  UPDATE_GPS_LOCATION: "Cập nhật vị trí GPS",
  UPDATE_WORK_SHIFT: "Cập nhật ca làm",
  ACTIVATE_WORK_SHIFT: "Kích hoạt ca làm",
  UPDATE_COMPANY: "Cập nhật công ty",
  UPDATE_ATTENDANCE: "Cập nhật chấm công",
  UPDATE_USER_AVATAR: "Cập nhật ảnh đại diện",
  UPDATE_MEAL_ALLOWANCE_CONFIG: "Cập nhật cấu hình phụ cấp cơm",
};

/**
 * Format mã hành động sang tiếng Việt (nếu có mapping).
 */
function formatAction(action: string) {
  return actionLabels[action] || action;
}

/**
 * Parse user agent để hiển thị nhanh thông tin thiết bị.
 */
function parseUserAgent(userAgent: string | null) {
  if (!userAgent)
    return { browser: "Unknown", os: "Unknown", device: "Unknown" };

  let browser = "Unknown";
  let os = "Unknown";
  let device = "Desktop";

  // Browser
  if (userAgent.includes("Chrome") && !userAgent.includes("Edg"))
    browser = "Chrome";
  else if (userAgent.includes("Firefox")) browser = "Firefox";
  else if (userAgent.includes("Safari") && !userAgent.includes("Chrome"))
    browser = "Safari";
  else if (userAgent.includes("Edg")) browser = "Edge";
  else if (userAgent.includes("Opera")) browser = "Opera";

  // OS
  if (userAgent.includes("Windows")) os = "Windows";
  else if (userAgent.includes("Mac")) os = "macOS";
  else if (userAgent.includes("Linux")) os = "Linux";
  else if (userAgent.includes("Android")) os = "Android";
  else if (
    userAgent.includes("iOS") ||
    userAgent.includes("iPhone") ||
    userAgent.includes("iPad")
  )
    os = "iOS";

  // Device
  if (
    userAgent.includes("Mobile") ||
    userAgent.includes("Android") ||
    userAgent.includes("iPhone")
  )
    device = "Mobile";
  else if (userAgent.includes("Tablet") || userAgent.includes("iPad"))
    device = "Tablet";

  return { browser, os, device };
}

// Layer: Page (UI) – chỉ render JSX, gọi hook domain từ features.
export const ActivityLogsPage: React.FC = () => {
  const {
    logs,
    loading,
    total,
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    searchText,
    setSearchText,
    selectedResourceType,
    setSelectedResourceType,
    selectedResult,
    setSelectedResult,
    dateRange,
    setDateRange,
    isFilterActive,
    handleClearFilters,
    selectedLog,
    isDetailModalOpen,
    handleViewDetail,
    handleCloseDetailModal,
    avatarCacheRef,
    fetchAvatarAsDataUri,
  }: UseActivityLogsResult = useActivityLogs();

  /**
   * Định nghĩa các cột cho bảng logs
   * Sử dụng useMemo để tránh re-render không cần thiết
   */
  const columns: ColumnsType<ActivityLog> = useMemo(
    () => [
      {
        title: "ID",
        dataIndex: "id",
        key: "id",
        width: 80,
        render: (id: number) => (
          <Text code className={styles.idText}>
            #{id}
          </Text>
        ),
        sorter: (a, b) => a.id - b.id,
      },
      {
        title: "Thời gian",
        dataIndex: "createdAt",
        key: "createdAt",
        width: 180,
        render: (date: string) => (
          <div className={styles.timeCell}>
            <Calendar size={14} className={styles.timeIcon} />
            <Text>{formatDateTimeDisplay(date)}</Text>
          </div>
        ),
        sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      },
      {
        title: "Người dùng",
        dataIndex: "user",
        key: "user",
        width: 230,
        render: (user: ActivityLog["user"]) => {
          if (!user) {
            return (
              <div className={styles.userCell}>
                <Avatar
                  size={32}
                  icon={<User size={16} />}
                  className={styles.avatar}
                />
                <div>
                  <Text type="secondary">Không xác định</Text>
                </div>
              </div>
            );
          }
          const avatarUrl = buildFullUrl(user.avatar);
          return (
            <div className={styles.userCell}>
              <ActivityLogAvatar
                avatarUrl={avatarUrl}
                fetchAvatarAsDataUri={fetchAvatarAsDataUri}
                cacheRef={avatarCacheRef}
              />
              <div>
                <Text strong>{user.fullName}</Text>
                <br />
                <Text type="secondary" className={styles.userEmail}>
                  {user.email}
                </Text>
              </div>
            </div>
          );
        },
      },
      {
        title: "Hành động",
        dataIndex: "action",
        key: "action",
        width: 200,
        render: (action: string | null | undefined) => {
          if (!action || action.trim() === "") {
            return <Text type="secondary">N/A</Text>;
          }
          const actionLabel = formatAction(action);
          return (
            <div className={styles.actionCell}>
              <Activity size={14} className={styles.actionIcon} />
              <Text>{actionLabel}</Text>
            </div>
          );
        },
      },
      {
        title: "Loại tài nguyên",
        dataIndex: "resourceType",
        key: "resourceType",
        width: 150,
        render: (type: string | null | undefined) => {
          if (!type || type.trim() === "") {
            return <Text type="secondary">N/A</Text>;
          }
          const label = resourceTypeLabels[type] || type;
          return <Tag color="blue">{label}</Tag>;
        },
      },
      {
        title: "Kết quả",
        dataIndex: "result",
        key: "result",
        width: 120,
        render: (result: string | null | undefined) => {
          if (!result || result.trim() === "") {
            return <Tag color="default">N/A</Tag>;
          }
          const config = resultConfig[result];
          if (!config) {
            return <Tag color="default">{result}</Tag>;
          }
          return (
            <Tag color={config.color} icon={config.icon}>
              {config.label}
            </Tag>
          );
        },
      },
      {
        title: "IP Address",
        dataIndex: "ip",
        key: "ip",
        width: 150,
        render: (ip: string | null) => (
          <div className={styles.ipCell}>
            <Globe size={14} className={styles.ipIcon} />
            <Text code>{ip || "N/A"}</Text>
          </div>
        ),
      },
      {
        title: "Thao tác",
        key: "actions",
        width: 100,
        fixed: "right",
        render: (_, record) => (
          <Tooltip title="Xem chi tiết">
            <Button
              type="text"
              icon={<Eye size={16} />}
              onClick={() => handleViewDetail(record)}
              className={styles.viewButton}
            />
          </Tooltip>
        ),
      },
    ],
    [fetchAvatarAsDataUri, handleViewDetail],
  );

  return (
    <div className={styles.container}>
      <FilterSection
        title="Bộ lọc"
        subtitle="Tìm kiếm và lọc nhật ký hoạt động"
        onClearFilter={handleClearFilters}
        isFilterActive={isFilterActive}
        customFilters={
          <>
            <Input
              placeholder="Tìm kiếm theo hành động, tên, mã hoặc email nhân sự..."
              prefix={
                <img
                  src={searchIcon}
                  alt="search"
                  className={styles.searchIcon}
                />
              }
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={() => {
                // Nhấn Enter: để debounce trong hook xử lý (UI không bị “giật”)
                handlePageChange(1);
              }}
              className={styles.filterInput}
              size="large"
            />
            <Select
              value={selectedResourceType}
              onChange={setSelectedResourceType}
              className={styles.filterSelect}
              size="large"
              placeholder="Loại tài nguyên"
            >
              <Option value="Tất cả">Tất cả</Option>
              <Option value="user">Người dùng</Option>
              <Option value="department">Phòng ban</Option>
              <Option value="company">Công ty</Option>
              <Option value="other">Khác</Option>
            </Select>
            <Select
              value={selectedResult}
              onChange={setSelectedResult}
              className={styles.filterSelect}
              size="large"
              placeholder="Kết quả"
            >
              <Option value="Tất cả">Tất cả</Option>
              <Option value="success">Thành công</Option>
              <Option value="failure">Thất bại</Option>
              <Option value="warning">Cảnh báo</Option>
            </Select>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="DD/MM/YYYY"
              className={styles.datePicker}
              size="large"
              placeholder={["Từ ngày", "Đến ngày"]}
            />
          </>
        }
      />

      {/* Bảng hiển thị danh sách logs */}
      <div className={styles.tableCard}>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={logs || []}
            rowKey="id"
            pagination={false}
            scroll={{ x: 1200 }}
            className={styles.table}
            locale={{
              emptyText: loading
                ? "Đang tải..."
                : "Không có dữ liệu nhật ký hoạt động",
            }}
          />
        </Spin>
        <Pagination
          current={currentPage}
          total={total}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          itemName="hoạt động"
          pageSizeOptions={["10", "20", "50", "100"]}
        />
      </div>

      {/* Modal hiển thị chi tiết log */}
      <Modal
        title={
          <div className={styles.modalTitle}>
            <FileText size={20} />
            <span>Chi tiết hoạt động</span>
          </div>
        }
        open={isDetailModalOpen}
        onCancel={handleCloseDetailModal}
        footer={[
          <Button key="close" onClick={handleCloseDetailModal}>
            Đóng
          </Button>,
        ]}
        width={800}
        className={styles.detailModal}
      >
        {selectedLog && (
          <div className={styles.detailContent}>
            <Descriptions column={1} bordered>
              <Descriptions.Item label="ID Log">
                <Text code>#{selectedLog.id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Thời gian">
                <div className={styles.detailItem}>
                  <Calendar size={16} />
                  <span>{formatDateTimeDisplay(selectedLog.createdAt)}</span>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Người dùng">
                {selectedLog.user ? (
                  <div className={styles.detailUser}>
                    <ActivityLogAvatar
                      avatarUrl={buildFullUrl(selectedLog.user.avatar)}
                      fetchAvatarAsDataUri={fetchAvatarAsDataUri}
                      cacheRef={avatarCacheRef}
                      size={40}
                    />
                    <div>
                      <div>
                        <Text strong>{selectedLog.user.fullName}</Text>
                      </div>
                      <div>
                        <Text type="secondary">{selectedLog.user.email}</Text>
                      </div>
                      {selectedLog.user.employeeCode && (
                        <div>
                          <Text type="secondary">
                            Mã NV: {selectedLog.user.employeeCode}
                          </Text>
                        </div>
                      )}
                      {selectedLog.userId && (
                        <div className={styles.userIdContainer}>
                          <Text type="secondary" className={styles.userIdText}>
                            User ID: {selectedLog.userId}
                          </Text>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    <Text type="secondary">Không xác định</Text>
                    {selectedLog.userId && (
                      <div className={styles.userIdContainer}>
                        <Text type="secondary" className={styles.userIdText}>
                          User ID: {selectedLog.userId}
                        </Text>
                      </div>
                    )}
                  </div>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Hành động">
                <Tag color="blue">{formatAction(selectedLog.action)}</Tag>
                <Text type="secondary" className={styles.actionCode}>
                  ({selectedLog.action})
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Loại tài nguyên">
                <Tag>
                  {resourceTypeLabels[selectedLog.resourceType] ||
                    selectedLog.resourceType}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="ID Tài nguyên">
                {selectedLog.resourceId ? (
                  <Text code>{selectedLog.resourceId}</Text>
                ) : (
                  <Text type="secondary">N/A</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Kết quả">
                {(() => {
                  const config = resultConfig[selectedLog.result];
                  if (!config) {
                    return <Tag>{selectedLog.result || "N/A"}</Tag>;
                  }
                  return (
                    <Tag color={config.color} icon={config.icon}>
                      {config.label}
                    </Tag>
                  );
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="IP Address">
                <div className={styles.detailItem}>
                  <Globe size={16} />
                  <Text code>{selectedLog.ip || "N/A"}</Text>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Thiết bị">
                {(() => {
                  const deviceInfo = parseUserAgent(selectedLog.userAgent);
                  return (
                    <div className={styles.deviceInfo}>
                      <div className={styles.deviceItem}>
                        <Monitor size={16} />
                        <span>
                          <Text strong>Trình duyệt: </Text>
                          {deviceInfo.browser}
                        </span>
                      </div>
                      <div className={styles.deviceItem}>
                        <Monitor size={16} />
                        <span>
                          <Text strong>Hệ điều hành: </Text>
                          {deviceInfo.os}
                        </span>
                      </div>
                      <div className={styles.deviceItem}>
                        <Monitor size={16} />
                        <span>
                          <Text strong>Thiết bị: </Text>
                          {deviceInfo.device}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </Descriptions.Item>
              {selectedLog.errorMessage && (
                <Descriptions.Item label="Thông báo lỗi">
                  <Text type="danger">{selectedLog.errorMessage}</Text>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Chi tiết">
                <TextArea
                  value={JSON.stringify(selectedLog.details, null, 2)}
                  readOnly
                  autoSize={{ minRows: 4, maxRows: 10 }}
                  className={styles.detailsTextArea}
                />
              </Descriptions.Item>
              <Descriptions.Item label="User Agent">
                <TextArea
                  value={selectedLog.userAgent || "N/A"}
                  readOnly
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  className={styles.detailsTextArea}
                />
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>
    </div>
  );
};
